import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Alert } from 'react-bootstrap-5';

import { isDateSet } from 'bundles/apex/dates/helpers';

import Button from 'shared/button';
import DateFormGroup from 'bundles/apex/dates/form_group';
import ImmediateRemindersModal from './immediate_reminders';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import Modal from 'components/modal';
import RemindersFields from 'bundles/apex/reminders/reminders_fields';
import { getFieldReadOnlyMessage, isFieldReadOnly } from 'bundles/apex/deals/helpers/fields';
import { checkDateChangeForImmediateReminders } from 'bundles/apex/reminders/helpers';

export default function CriticalDateModal({
  addCriticalDate,
  checkImmediateRemindersPath,
  criticalDateName,
  deal,
  fields,
  onHide,
  reminders,
  setCriticalDate,
  show,
}) {
  const [name, setName] = useState(criticalDateName);
  const [date, setDate] = useState(deal[criticalDateName]);
  const [customName, setCustomName] = useState('');
  const [requestReminders, setRequestReminders] = useState([]);
  const [userNames, setUserNames] = useState([]);
  const [showImmediateReminders, setShowImmediateReminders] = useState(false);
  const [saving, setSaving] = useState();
  const [errors, setErrors] = useState();

  const isCustom = name === 'other';

  async function handleAdd() {
    try {
      const dateForRequest = {
        ...date,
        remindersAttributes: requestReminders,
      };

      setSaving(true);

      if (isCustom) {
        await addCriticalDate(customName, dateForRequest);
      } else {
        const { reminders, users } =
          await checkDateChangeForImmediateReminders(checkImmediateRemindersPath, dateForRequest, requestReminders);

        setUserNames(users || []);
        if (reminders.length > 0) {
          setShowImmediateReminders(true);
          return;
        }

        await setCriticalDate(name, dateForRequest);
      }

      setSaving(false);
      onHide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    }
  }

  function reset() {
    setName(criticalDateName);
    setDate(deal[criticalDateName]);
    setCustomName('');
    setRequestReminders(reminders?.filter(reminder => !reminder.sent) || []);
    setSaving(false);
    setErrors();
  }

  async function handleImmediateReminders(keepImmediateReminders) {
    const dateForRequest = {
      ...date,
      remindersAttributes: requestReminders,
      keepImmediateReminders,
    };

    setShowImmediateReminders(false);

    try {
      await setCriticalDate(name, dateForRequest);

      setSaving(false);
      onHide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    } finally {
      setUserNames([]);
    }
  }

  function hideReminders() {
    setShowImmediateReminders(false);
    setSaving(false);
  }

  let dateOptions = [];
  if (!criticalDateName) {
    dateOptions = Object.keys(fields).filter((name) => {
      const field = fields[name];
      return field.isDate && !isDateSet(deal[name]) && (!field.hidden || !field.custom);
    });
  }

  const reminderRelativeKey = isCustom ? customName || '__custom__' : name;

  return (
    <Modal show={show} onHide={onHide} onShow={reset}>
      <Modal.Header closeButton>
        { criticalDateName ? 'Edit Critical Date' : 'Add Critical Date' }
      </Modal.Header>

      <Modal.Body>
        <div className="form-vertical-field-container">
          {!criticalDateName && (
            <ManagedFormGroup
              includeBlank
              label="Critical Date Type"
              name="name"
              type="select"
              value={name}
              onValueChange={setName}
            >
              {dateOptions.map(name => (
                <option key={name} value={name}>
                  {fields[name].label}
                </option>
              ))}
              <option key="other" value="other">Other</option>
            </ManagedFormGroup>
          )}
          {isCustom && (
            <ManagedFormGroup
              label="Date Label"
              name="customName"
              required
              value={customName}
              onValueChange={setCustomName}
            />
          )}
          {name && (
            <DateFormGroup
              aria-label="date"
              className="date-field"
              deal={deal}
              disabled={isFieldReadOnly(name, deal)}
              disabledMessage={getFieldReadOnlyMessage(name)}
              fields={fields}
              label={isCustom ? 'Date' : (date && date.label) || fields[name]?.label}
              name={isCustom ? customName : name}
              required
              value={date || deal[name] || { operator: 'before', units: 'day' }}
              onValueChange={setDate}
            />
          )}

          {name && (
            <RemindersFields
              dateNameWhitelist={[reminderRelativeKey]}
              deal={{ ...deal, [reminderRelativeKey]: date }}
              fields={{
                ...fields,
                [reminderRelativeKey]: {
                  label: isCustom ? (customName ||  'date') : ((date && date.label) || fields[name]?.label),
                  isDate: true
                }
              }}
              reminders={requestReminders}
              onChange={setRequestReminders}
            />
          )}
        </div>

        {(errors || []).map((error, i) => (
          <Alert className="mt-3" key={i} variant="warning">{error}</Alert>
        ))}

        <ImmediateRemindersModal
          handleSelection={handleImmediateReminders}
          show={showImmediateReminders}
          userNames={userNames}
          onHide={hideReminders}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button.Cancel name="cancel" onClick={onHide} />
        <Button.Primary disabled={!date || saving || (isCustom && !customName)} name="save" onClick={handleAdd}>
          Save Critical Date
        </Button.Primary>
      </Modal.Footer>
    </Modal>
  );
}

CriticalDateModal.propTypes = {
  addCriticalDate: PropTypes.func.isRequired,
  checkImmediateRemindersPath: PropTypes.string,
  criticalDateName: PropTypes.string,
  deal: PropTypes.shape({
    tasks: PropTypes.arrayOf(PropTypes.shape({
      deadline: PropTypes.shape({
        // TODO: Fix serialization - deadline.date should be the date string, not a date object
        date: PropTypes.shape({
          date: PropTypes.string,
        }),
      }),
    })),
  }),
  fields: PropTypes.object,
  reminders: PropTypes.array,
  setCriticalDate: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired
};
